import axios from 'axios';

/**
 * The no-login payment page's API: whoever holds the link can see what's owed
 * and pay it. Not tied to a sign-in: the link names the event. These calls go
 * to `/api/v3/pay-links` on the API host axios is pointed at (main.tsx).
 */

/** One line of the payment schedule (deposit, final balance, or a single payment). */
export interface PayLinkLine {
  readonly key: string;
  readonly label: string;
  readonly amount: number;
  readonly outstanding: number;
  readonly paid: boolean;
  readonly dueDate: string | null;
  readonly dueNote: string | null;
}

/** Which page a link opens: one event's balance, or a series contract's events together. */
export const PAY_LINK_KIND = { event: 'event', series: 'series' } as const;

/** An event's balance: what's been paid and the lines still open. */
export interface PayLinkSchedule {
  readonly contractedAmount: number;
  readonly amountPaid: number;
  readonly amountDue: number;
  readonly lines: readonly PayLinkLine[];
}

/** What the page shows about one event's balance. */
export interface PayLinkView {
  readonly kind: typeof PAY_LINK_KIND.event;
  readonly contractNumber: string | null;
  readonly accountName: string | null;
  readonly programName: string | null;
  readonly eventDate: string | null;
  readonly publishableKey: string | null;
  /** The credit-card surcharge, said up front; 0 = none. */
  readonly cardSurchargePercent: number;
  readonly schedule: PayLinkSchedule;
  readonly paidInFull: boolean;
  /** When a bank payment started through this link that hasn't cleared yet. */
  readonly processingSince: string | null;
}

/** One event of a series on the payment page. */
export interface SeriesEventView {
  readonly eventId: string;
  readonly contractNumber: string | null;
  readonly programName: string | null;
  readonly eventDate: string | null;
  readonly schedule: PayLinkSchedule;
  readonly paidInFull: boolean;
}

/** What the page shows for a series contract: its events, paid together. */
export interface SeriesPayLinkView {
  readonly kind: typeof PAY_LINK_KIND.series;
  readonly accountName: string | null;
  readonly publishableKey: string | null;
  readonly cardSurchargePercent: number;
  readonly events: readonly SeriesEventView[];
  readonly paidInFull: boolean;
  readonly processingSince: string | null;
}

export type AnyPayLinkView = PayLinkView | SeriesPayLinkView;

/** One event of a series payment and what to pay on it. */
export type SeriesChoice = PayLinkChoice & { readonly eventId: string };

/** What to pay: one schedule line, everything owed, or another amount. */
export type PayLinkChoice =
  | { readonly line: string; readonly amount: number }
  | { readonly line: 'total'; readonly amount: number }
  | { readonly line: 'custom'; readonly amount: number };

/** Why the page can't show a balance. */
export const PAY_LINK_ERROR = {
  /** Revoked, forged, or the event no longer takes payments here. */
  inactive: 'inactive',
  unavailable: 'unavailable',
} as const;
export type PayLinkError = (typeof PAY_LINK_ERROR)[keyof typeof PAY_LINK_ERROR];

export class PayLinkRequestError extends Error {
  constructor(readonly kind: PayLinkError, message: string) {
    super(message);
    this.name = 'PayLinkRequestError';
  }
}

const HTTP_NOT_FOUND = 404;

function base(token: string): string {
  return `/api/v3/pay-links/${encodeURIComponent(token)}`;
}

/** The API's own message, else a generic one. */
function toError(err: unknown): PayLinkRequestError {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { error?: string } | undefined)?.error;
    if (err.response?.status === HTTP_NOT_FOUND) {
      return new PayLinkRequestError(PAY_LINK_ERROR.inactive, message ?? 'This payment link is no longer active.');
    }
    if (message) return new PayLinkRequestError(PAY_LINK_ERROR.unavailable, message);
  }
  return new PayLinkRequestError(PAY_LINK_ERROR.unavailable, 'We could not reach the payment service. Please try again.');
}

export async function readPayLink(token: string, signal?: AbortSignal): Promise<AnyPayLinkView> {
  try {
    const response = await axios.get<{ data: AnyPayLinkView }>(base(token), signal ? { signal } : {});
    return response.data.data;
  } catch (err) {
    throw toError(err);
  }
}

/** What a payment costs once the payment method is known; nothing is charged. */
export interface PayLinkQuote {
  readonly amount: number;
  readonly fee: number;
  readonly total: number;
  readonly feePercent: number;
  readonly totalCents: number;
}

/** The request body's "what to pay" part. */
function payBody(choice: PayLinkChoice): { line: string; amount?: number } {
  return { line: choice.line, ...(choice.line === 'custom' ? { amount: choice.amount } : {}) };
}

export async function quotePayLinkPayment(token: string, choice: PayLinkChoice, confirmationTokenId: string): Promise<PayLinkQuote> {
  try {
    const response = await axios.post<{ data: PayLinkQuote }>(`${base(token)}/quote`, { ...payBody(choice), confirmationTokenId });
    return response.data.data;
  } catch (err) {
    throw toError(err);
  }
}

export async function payPayLink(
  token: string,
  choice: PayLinkChoice,
  confirmationTokenId: string,
  expectedTotalCents: number,
  email: string,
): Promise<{ paymentIntentId: string; clientSecret: string | null; status: string }> {
  try {
    const response = await axios.post<{ data: { paymentIntentId: string; clientSecret: string | null; status: string } }>(`${base(token)}/pay`, {
      ...payBody(choice),
      confirmationTokenId,
      expectedTotalCents,
      email,
    });
    return response.data.data;
  } catch (err) {
    throw toError(err);
  }
}

export async function confirmPayLinkPayment(token: string, paymentIntentId: string): Promise<string> {
  try {
    const response = await axios.post<{ data: { status: string } }>(`${base(token)}/confirm/${encodeURIComponent(paymentIntentId)}`);
    return response.data.data.status;
  } catch (err) {
    throw toError(err);
  }
}

function seriesBody(choices: readonly SeriesChoice[]): { selections: { eventId: string; line: string; amount?: number }[] } {
  return { selections: choices.map((choice) => ({ eventId: choice.eventId, ...payBody(choice) })) };
}

/** What paying the chosen events together costs once the payment method is known; nothing is charged. */
export async function quoteSeriesPayment(token: string, choices: readonly SeriesChoice[], confirmationTokenId: string): Promise<PayLinkQuote> {
  try {
    const response = await axios.post<{ data: PayLinkQuote }>(`${base(token)}/series/quote`, { ...seriesBody(choices), confirmationTokenId });
    return response.data.data;
  } catch (err) {
    throw toError(err);
  }
}

/** Pays the chosen events in one charge. */
export async function paySeriesPayment(
  token: string,
  choices: readonly SeriesChoice[],
  confirmationTokenId: string,
  expectedTotalCents: number,
  email: string,
): Promise<{ paymentIntentId: string; clientSecret: string | null; status: string }> {
  try {
    const response = await axios.post<{ data: { paymentIntentId: string; clientSecret: string | null; status: string } }>(`${base(token)}/series/pay`, {
      ...seriesBody(choices),
      confirmationTokenId,
      expectedTotalCents,
      email,
    });
    return response.data.data;
  } catch (err) {
    throw toError(err);
  }
}
